/**
 * Bottom-sheet modal for writing and submitting a translation boost.
 * The language picker excludes the original post's language. Renders via createPortal.
 */
import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'motion/react'
import { X, Loader2 } from 'lucide-react'
import { POST_CHAR_LIMIT, LANGUAGES } from '@olimhub/shared'
import type { Language } from '@olimhub/shared'
import axios from 'axios'
import { translationsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { useTranslation } from 'react-i18next'

const UI_LANGUAGES = LANGUAGES.filter((l) => l !== 'he')

interface TranslationBoostModalProps {
  open: boolean
  onClose: () => void
  postId: string
  originalContent: string
  originalLanguage: Language
  // Present (string or null) only when translating an event — lets the
  // translator localize the free-text venue, or leave it as the original.
  eventPlaceText?: string | null
  onSuccess?: () => void
}

export function TranslationBoostModal({ open, onClose, postId, originalContent, originalLanguage, eventPlaceText, onSuccess }: TranslationBoostModalProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const availableLanguages = useMemo(
    () => UI_LANGUAGES.filter((l) => l !== originalLanguage),
    [originalLanguage],
  )
  const defaultLanguage = availableLanguages[0] ?? 'ru'
  const [language, setLanguage] = useState<Language>(defaultLanguage)
  const [content, setContent] = useState('')
  const [placeText, setPlaceText] = useState(eventPlaceText ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEvent = eventPlaceText !== undefined

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      setContent('')
      setLanguage(availableLanguages[0] ?? 'ru')
      setPlaceText(eventPlaceText ?? '')
    }
  }, [open, availableLanguages, eventPlaceText])

  const remaining = POST_CHAR_LIMIT - content.length
  const isOverLimit = remaining < 0
  const isEmpty = content.trim().length === 0

  const handleSubmit = async () => {
    if (isEmpty || isOverLimit || isSubmitting) return
    setIsSubmitting(true)
    try {
      await translationsApi.create(postId, content, language, isEvent ? (placeText || null) : undefined)
      toast.success('Translation shared!')
      onSuccess?.()
      onClose()
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        toast.error('Translation must be in a different language than the original.')
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200,
              background: 'rgba(8,10,15,0.52)',
              backdropFilter: 'blur(2px)',
            }}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            style={{
              position: 'fixed',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              bottom: 0,
              zIndex: 201,
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              boxShadow: 'var(--shadow-modal)',
              maxHeight: '90dvh',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Handle bar */}
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'var(--border-strong)',
                margin: '10px auto 0',
                flexShrink: 0,
              }}
            />

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px 0',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  color: 'var(--text-strong)',
                  flex: 1,
                }}
              >
                {t('post.add_translation')}
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 0' }}>
              {/* Original text reference */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-sunk)',
                  border: '1px solid var(--border)',
                  marginBottom: 12,
                }}
              >
                <p
                  style={{
                    margin: '0 0 4px',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 600,
                    color: 'var(--text-faint)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {t('post.original_label')}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                    lineHeight: 'var(--lh-normal)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {originalContent}
                </p>
              </div>

              {/* Language selector */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {availableLanguages.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-pill)',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 600,
                      background: language === lang ? 'var(--brand)' : 'var(--surface-2)',
                      color: language === lang ? 'var(--on-brand)' : 'var(--text-muted)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('post.translation_placeholder')}
                rows={4}
                style={{
                  width: '100%',
                  minHeight: 100,
                  resize: 'vertical',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1.5px solid var(--border)',
                  background: 'var(--bg-sunk)',
                  color: 'var(--text-strong)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body)',
                  lineHeight: 'var(--lh-normal)',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
              <div
                style={{
                  textAlign: 'end',
                  fontSize: 'var(--fs-xs)',
                  fontFamily: 'var(--font-sans)',
                  color: remaining < 20 ? 'var(--danger)' : 'var(--text-faint)',
                  marginTop: 4,
                }}
              >
                {content.length}/{POST_CHAR_LIMIT}
              </div>

              {isEvent && (
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 6,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 600,
                      color: 'var(--text-faint)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {t('events.place')}
                  </label>
                  <input
                    type="text"
                    value={placeText}
                    onChange={(e) => setPlaceText(e.target.value)}
                    placeholder={t('events.place_placeholder')}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-md)',
                      border: '1.5px solid var(--border)',
                      background: 'var(--bg-sunk)',
                      color: 'var(--text-strong)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-body)',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                </div>
              )}

              <div style={{ height: 16 }} />
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid var(--border)',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                disabled={isEmpty || isOverLimit || isSubmitting}
                onClick={handleSubmit}
                style={{
                  width: '100%',
                  padding: '11px 0',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: isEmpty || isOverLimit ? 'var(--surface-2)' : 'var(--brand)',
                  color: isEmpty || isOverLimit ? 'var(--text-faint)' : 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  cursor: isEmpty || isOverLimit || isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'background var(--dur-fast)',
                }}
              >
                {isSubmitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {t('post.share_translation')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
